// A project's Feedback tab on the Work page (MOTHER-327): what people using the client's app sent
// about it (GET /api/client-feedback), New, Triaged or Dismissed, newest first. An Admin turns a new
// row into a backlog work item on the project, or dismisses it; a triaged row links to its item.
// Admins only: WorkPage shows the tab to no one else, and the server refuses everyone else too.
import {
  Button, type ColumnDef, DataTable, EmptyState, FeedbackIcon, InlineMessage, SegmentedButtonGroup, Spinner, Stack, Text, TextLink,
} from '@olytic/foundation';
import { useEffect, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { ObjectsApiError } from '../objects/objectsApi';
import { excerpt, shortDate } from './model';
import {
  type ClientFeedback, type ClientFeedbackStatus, convertClientFeedback, dismissClientFeedback, fetchClientFeedback, type WorkProject,
} from './workApi';

export const FEEDBACK_STATUSES: readonly { value: ClientFeedbackStatus; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'triaged', label: 'Triaged' },
  { value: 'dismissed', label: 'Dismissed' },
];

const EMPTY: Record<ClientFeedbackStatus, { title: string; description: string }> = {
  new: { title: 'No feedback yet', description: 'Feedback from the client\'s app shows here for you to turn into work or dismiss.' },
  triaged: { title: 'No triaged feedback', description: 'Feedback turned into a work item shows here.' },
  dismissed: { title: 'No dismissed feedback', description: 'Feedback you dismiss shows here.' },
};

/** Who sent it: their name, else their email, with their role in the client's app. */
export function submitterOf(row: Pick<ClientFeedback, 'submitterName' | 'submitterEmail' | 'submitterRole'>): string {
  const who = row.submitterName ?? row.submitterEmail ?? 'Unknown';
  return row.submitterRole ? `${who} (${row.submitterRole})` : who;
}

const receivedText = (iso: string) => `${shortDate(iso)}, ${iso.slice(0, 4)}`;

export function WorkFeedback({
  project, onOpen, onConverted, say,
}: {
  project: WorkProject;
  /** Opens a work item on the Work page. */
  onOpen: (itemId: string) => void;
  /** A work item was made: the page reloads its items. */
  onConverted: () => void;
  say: (text: string, tone?: 'info' | 'error') => void;
}): JSX.Element {
  const [status, setStatus] = useState<ClientFeedbackStatus>('new');
  const [rows, setRows] = useState<ClientFeedback[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  // Rows with a convert or dismiss on its way, so a second click sends nothing.
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    let live = true;
    setRows(null);
    setError(null);
    fetchClientFeedback(authenticatedFetch, project.id, status)
      .then((list) => live && setRows(list))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : 'Could not load the feedback.'));
    return () => {
      live = false;
    };
  }, [project.id, status, reload]);

  const act = async (row: ClientFeedback, action: 'convert' | 'dismiss') => {
    if (busy.has(row.id)) return;
    setBusy((ids) => new Set(ids).add(row.id));
    try {
      if (action === 'convert') {
        const { workItemKey } = await convertClientFeedback(authenticatedFetch, row.id);
        say(`Created ${workItemKey} from the feedback`);
        onConverted();
      } else {
        await dismissClientFeedback(authenticatedFetch, row.id);
        say('Dismissed the feedback');
      }
    } catch (e) {
      const already = e instanceof ObjectsApiError && e.status === 409;
      say(already ? 'Someone has already triaged this feedback.' : e instanceof Error ? e.message : 'Could not save the change.', 'error');
    } finally {
      setBusy((ids) => {
        const next = new Set(ids);
        next.delete(row.id);
        return next;
      });
      setReload((n) => n + 1);
    }
  };

  const columns: ColumnDef<ClientFeedback>[] = [
    { key: 'receivedAt', label: 'Received', get: (r) => r.receivedAt, render: (r) => receivedText(r.receivedAt), nowrap: true },
    { key: 'submitter', label: 'Submitter', get: (r) => submitterOf(r) },
    { key: 'body', label: 'Message', get: (r) => r.body, render: (r) => <span title={r.body}>{excerpt(r.body)}</span> },
    {
      key: 'pageUrl',
      label: 'Page URL',
      get: (r) => r.pageUrl ?? '',
      render: (r) => (r.pageUrl ? <TextLink href={r.pageUrl} target="_blank" rel="noreferrer noopener" className="break-all">{r.pageUrl}</TextLink> : null),
    },
    status === 'new'
      ? {
          key: 'actions',
          label: 'Actions',
          nowrap: true,
          get: () => '',
          render: (r) => (
            <span className="flex flex-wrap gap-2">
              <Button size="sm" variant="brand" disabled={busy.has(r.id)} onClick={() => void act(r, 'convert')}>Convert to work item</Button>
              <Button size="sm" variant="neutral" disabled={busy.has(r.id)} onClick={() => void act(r, 'dismiss')}>Dismiss</Button>
            </span>
          ),
        }
      : {
          key: 'workItemKey',
          label: 'Work Item',
          nowrap: true,
          get: (r) => r.workItemKey ?? '',
          render: (r) => (r.workItemKey && r.workItemId ? <Button variant="link" onClick={() => onOpen(r.workItemId!)}>{r.workItemKey}</Button> : null),
        },
  ];

  return (
    <Stack gap="md" className="pt-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Text as="h3" className="m-0 font-emphasis text-[var(--fg-strong)]">Client Feedback</Text>
        <SegmentedButtonGroup<ClientFeedbackStatus> label="Status" size="sm" options={FEEDBACK_STATUSES} value={status} onChange={setStatus} />
      </div>
      {error ? (
        <InlineMessage tone="error" role="alert">{error}</InlineMessage>
      ) : rows === null ? (
        <div className="flex justify-center py-10"><Spinner label="Loading feedback" /></div>
      ) : rows.length === 0 ? (
        <EmptyState icon={<FeedbackIcon />} title={EMPTY[status].title} description={EMPTY[status].description} />
      ) : (
        <DataTable<ClientFeedback>
          aria-label={`${FEEDBACK_STATUSES.find((s) => s.value === status)?.label} feedback`}
          columns={columns}
          rows={rows}
          getRowId={(r) => r.id}
          fit
        />
      )}
    </Stack>
  );
}
